import { checkPermissions } from "@elit-hair/elit-tools";
import { GridActionsCellItem, GridCellModes, GridColDef, useGridApiContext } from "@mui/x-data-grid";
import { GridRowModes } from "@mui/x-data-grid";
import { createElement, useMemo } from "react";
import { IconType } from "react-icons/lib";
import { TbEdit, TbEye, TbEyeOff, TbFriends, TbHistory, TbTrash, TbX } from "react-icons/tb";

import { PERMISSIONS } from "@/constants/permissions";
import { checkBit } from "@/helpers/bitwise";
import useAuth from "@/providers/hooks/useAuth";
import { usePlanActions, usePlanRowChanges, usePlanRowModesModel } from "@/stores/use-plan-table";
import { PassengerExtras, PassengerTypes } from "@/types/global";
import { IPlanPassenger } from "@/types/tasks.types";

import { CommonFilterSwitch } from "../../types";

export const PlanActionsColumn = (): GridColDef<IPlanPassenger<"api">> => {
    return {
        field: "actions",
        type: "actions",
        width: 60,
        cellClassName: "!px-2",
        pinnable: true,
        getActions: ({ row }) => {
            const rowModesModel = usePlanRowModesModel();
            const {
                handleRowModesModelChange,
                handleRowModesModelCancel,
                updateShowUpdateCompanionsDialog,
                updatShowDeletePassengerDialog,
                updateShowPassengerToWelcomeTeamDialog,
                updateShowPassengerHistoryDialog,
            } = usePlanActions();
            const apiRef = useGridApiContext();
            const rowChanges = usePlanRowChanges(row.id);
            const isInEditMode =
                rowModesModel[row.id]?.mode === GridRowModes.Edit ||
                ["driverId", "location", "airport"].some(
                    (item) => apiRef.current.getCellMode(row.id, item) === GridCellModes.Edit
                );

            const userPermissions = useAuth()?.userData?.permissionCodes || [];
            const canUpdateCompanions = checkPermissions(userPermissions, [
                PERMISSIONS.TR_SVC_UPDATE_PASSENGER_COMPANIONS,
            ]);

            const handleCompanions = () => {
                const { id, name, fullCompanions, passengerType, cancelled } = row;
                updateShowUpdateCompanionsDialog({
                    id: id as string,
                    name,
                    cancelled,
                    passengerType,
                    fullCompanions,
                });
            };

            const handleEdit = () => {
                handleRowModesModelChange({ ...rowModesModel, [row.id]: { mode: GridRowModes.Edit } });
            };

            const handleCancel = () => {
                handleRowModesModelCancel(row.id, apiRef.current);
            };
            const handleDelete = () => {
                updatShowDeletePassengerDialog({
                    id: row.id,
                    name: row.name,
                    taskAssigned: !!row.taskId,
                });
            };
            const handleShowToPassengers = (showToWelcomeTeam: CommonFilterSwitch) => {
                updateShowPassengerToWelcomeTeamDialog({
                    id: row.id,
                    name: row.name,
                    showToWelcomeTeam,
                });
            };
            const handleShowHistory = () => {
                updateShowPassengerHistoryDialog(row.id);
            };

            const actions: {
                label: string;
                icon: IconType;
                color: "primary" | "warning" | "info";
                cond: boolean;
                onClick: () => void;
                showInMenu: boolean;
            }[] = useMemo(
                () => [
                    {
                        label: "Güncelle",
                        icon: TbEdit,
                        onClick: handleEdit,
                        cond: !rowChanges && !isInEditMode,
                        color: "primary",
                        showInMenu: false,
                    },
                    {
                        label: "Sil",
                        icon: TbTrash,
                        onClick: handleDelete,
                        cond: !rowChanges && !isInEditMode && row.passengerType === PassengerTypes.MANUAL,
                        color: "primary",
                        showInMenu: true,
                    },
                    {
                        label: "Yolcuyu Karşılama ekibine göster",
                        icon: TbEye,
                        onClick: () => handleShowToPassengers(CommonFilterSwitch.TRUE),
                        cond:
                            !rowChanges &&
                            !isInEditMode &&
                            row.isFlightArrival &&
                            row.passengerType === PassengerTypes.MANUAL &&
                            checkBit(row.extras || 0, PassengerExtras.HIDE_FOR_WELCOME_TEAM),
                        color: "primary",
                        showInMenu: true,
                    },
                    {
                        label: "Yolcuyu Karşılama ekibinden gizle",
                        icon: TbEyeOff,
                        onClick: () => handleShowToPassengers(CommonFilterSwitch.FALSE),
                        cond:
                            !rowChanges &&
                            !isInEditMode &&
                            row.isFlightArrival &&
                            row.passengerType === PassengerTypes.MANUAL &&
                            !checkBit(row.extras || 0, PassengerExtras.HIDE_FOR_WELCOME_TEAM),
                        color: "primary",
                        showInMenu: true,
                    },

                    {
                        label: "Refakatçi Güncelle",
                        icon: TbFriends,
                        onClick: handleCompanions,
                        cond: !rowChanges && !isInEditMode && canUpdateCompanions,
                        color: "primary",
                        showInMenu: true,
                    },
                    {
                        label: "İptal Et",
                        icon: TbX,
                        onClick: handleCancel,
                        cond: !!rowChanges || isInEditMode,
                        color: "warning",
                        showInMenu: false,
                    },
                    {
                        label: "Geçmişi Görüntüle",
                        icon: TbHistory,
                        onClick: handleShowHistory,
                        cond: !isInEditMode,
                        color: "primary",
                        showInMenu: true,
                    },
                ],
                [
                    isInEditMode,
                    row,
                    handleShowHistory,
                    handleEdit,
                    handleDelete,
                    rowChanges,
                    handleCompanions,
                    canUpdateCompanions,
                    handleCancel,
                    handleShowToPassengers,
                ]
            );

            return actions
                .filter((action) => action.cond)
                .map((action) => (
                    <GridActionsCellItem
                        icon={createElement(action.icon)}
                        label={action.label}
                        onClick={action.onClick}
                        color={action.color}
                        showInMenu={action.showInMenu}
                        key={action.label}
                    />
                ));
        },
    };
};
